import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  BRANCH_LABELS,
  GUNSONU_WHATSAPP_SETTINGS_ID,
  normalizePhoneForWhatsapp,
} from '../utils/gunsonuWhatsappReport';
import { gunsonuWhatsappSettingsRef } from '../utils/gunsonuWhatsappSettings';

const BRANCHES = [
  { key: 'sefakoy_merkez', label: BRANCH_LABELS.sefakoy_merkez },
  { key: 'sefakoy_sube', label: BRANCH_LABELS.sefakoy_sube },
  { key: 'kavakli_sube', label: BRANCH_LABELS.kavakli_sube },
];

function emptyDraft() {
  return {
    enabled: true,
    recipients: [],
    newName: '',
    newPhone: '',
    dirty: false,
    loading: true,
    saving: false,
    message: null,
  };
}

/**
 * Yönetici: şube bazlı günsonu WhatsApp alıcı numaraları.
 */
export default function WhatsappAyarlari() {
  const [byBranch, setByBranch] = useState(() =>
    Object.fromEntries(BRANCHES.map((b) => [b.key, emptyDraft()]))
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = {};
      await Promise.all(
        BRANCHES.map(async (b) => {
          try {
            const snap = await getDoc(gunsonuWhatsappSettingsRef(b.key));
            const data = snap.exists() ? snap.data() : {};
            const recipients = Array.isArray(data.recipients)
              ? data.recipients.map((r) => ({
                  id: String(r.id || crypto.randomUUID()),
                  name: String(r.name || '').trim() || 'Alıcı',
                  phone: String(r.phone || '').trim(),
                }))
              : [];
            next[b.key] = {
              ...emptyDraft(),
              enabled: data.enabled !== false,
              recipients,
              loading: false,
            };
          } catch (err) {
            next[b.key] = {
              ...emptyDraft(),
              loading: false,
              message: { type: 'error', text: err?.message || 'Yüklenemedi' },
            };
          }
        })
      );
      if (!cancelled) setByBranch(next);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const patchBranch = (subeKey, patch) => {
    setByBranch((prev) => ({
      ...prev,
      [subeKey]: { ...prev[subeKey], ...patch },
    }));
  };

  const handleAdd = (subeKey) => {
    const draft = byBranch[subeKey];
    const phone = normalizePhoneForWhatsapp(draft.newPhone);
    if (!phone) {
      patchBranch(subeKey, {
        message: { type: 'error', text: 'Geçerli telefon girin (örn. 05xx xxx xx xx).' },
        dirty: draft.dirty,
      });
      return;
    }
    patchBranch(subeKey, {
      recipients: [
        ...draft.recipients,
        {
          id: crypto.randomUUID(),
          name: draft.newName.trim() || 'Alıcı',
          phone: draft.newPhone.trim(),
        },
      ],
      newName: '',
      newPhone: '',
      message: null,
      dirty: true,
    });
  };

  const handleRemove = (subeKey, id) => {
    const draft = byBranch[subeKey];
    patchBranch(subeKey, {
      recipients: draft.recipients.filter((r) => r.id !== id),
      message: null,
      dirty: true,
    });
  };

  const handleSave = async (subeKey) => {
    const draft = byBranch[subeKey];
    const cleaned = draft.recipients
      .map((r) => ({
        id: r.id || crypto.randomUUID(),
        name: String(r.name || '').trim() || 'Alıcı',
        phone: String(r.phone || '').trim(),
      }))
      .filter((r) => normalizePhoneForWhatsapp(r.phone));

    setByBranch((prev) => ({
      ...prev,
      [subeKey]: { ...prev[subeKey], saving: true, message: null },
    }));

    try {
      await setDoc(
        doc(db, 'subeler', subeKey, 'ayarlar', GUNSONU_WHATSAPP_SETTINGS_ID),
        {
          enabled: draft.enabled !== false,
          recipients: cleaned,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setByBranch((prev) => ({
        ...prev,
        [subeKey]: {
          ...prev[subeKey],
          recipients: cleaned,
          saving: false,
          dirty: false,
          message: { type: 'success', text: 'Kaydedildi.' },
        },
      }));
    } catch (err) {
      setByBranch((prev) => ({
        ...prev,
        [subeKey]: {
          ...prev[subeKey],
          saving: false,
          message: { type: 'error', text: err?.message || 'Kayıt başarısız.' },
        },
      }));
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: 980,
        mx: 'auto',
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
        <WhatsAppIcon sx={{ color: '#128C7E', fontSize: 28 }} />
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          WhatsApp Ayarları
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2.5, fontWeight: 600 }}>
        Günsonu kasa kaydından sonra personelin WhatsApp ile göndereceği raporun alıcı numaralarını
        şube bazında buradan yönetin. Personel yalnızca kayıtlı alıcılara &quot;Rapor Gönder&quot; yapar.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {BRANCHES.map((branch) => {
          const draft = byBranch[branch.key] || emptyDraft();
          return (
            <Paper
              key={branch.key}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid #d9e2ee',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>
                    {branch.label}
                  </Typography>
                  {draft.dirty ? (
                    <Chip size="small" label="Kaydedilmedi" color="warning" sx={{ fontWeight: 700 }} />
                  ) : null}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.enabled !== false}
                      onChange={(e) =>
                        patchBranch(branch.key, { enabled: e.target.checked, dirty: true })
                      }
                      color="success"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      Günsonu WhatsApp raporu açık
                    </Typography>
                  }
                />
              </Box>

              {draft.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {draft.message ? (
                    <Alert severity={draft.message.type} sx={{ mb: 1.5 }}>
                      {draft.message.text}
                    </Alert>
                  ) : null}

                  {draft.recipients.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1.5 }}>
                      Bu şube için henüz alıcı yok.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                      {draft.recipients.map((r) => (
                        <Box
                          key={r.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            bgcolor: '#fff',
                          }}
                        >
                          <TextField
                            size="small"
                            label="İsim"
                            value={r.name}
                            onChange={(e) => {
                              const name = e.target.value;
                              patchBranch(branch.key, {
                                recipients: draft.recipients.map((x) =>
                                  x.id === r.id ? { ...x, name } : x
                                ),
                                dirty: true,
                              });
                            }}
                            sx={{ flex: '1 1 140px' }}
                          />
                          <TextField
                            size="small"
                            label="Telefon"
                            value={r.phone}
                            onChange={(e) => {
                              const phone = e.target.value;
                              patchBranch(branch.key, {
                                recipients: draft.recipients.map((x) =>
                                  x.id === r.id ? { ...x, phone } : x
                                ),
                                dirty: true,
                              });
                            }}
                            sx={{ flex: '1 1 180px' }}
                          />
                          <IconButton
                            aria-label="Sil"
                            onClick={() => handleRemove(branch.key, r.id)}
                            color="error"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1, color: '#334155' }}>
                    Alıcı ekle
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="İsim"
                      placeholder="Patron"
                      value={draft.newName}
                      onChange={(e) =>
                        patchBranch(branch.key, {
                          newName: e.target.value,
                          dirty: draft.dirty,
                          message: null,
                        })
                      }
                      sx={{ flex: '1 1 140px' }}
                    />
                    <TextField
                      size="small"
                      label="Telefon"
                      placeholder="05xx xxx xx xx"
                      value={draft.newPhone}
                      onChange={(e) =>
                        patchBranch(branch.key, {
                          newPhone: e.target.value,
                          dirty: draft.dirty,
                          message: null,
                        })
                      }
                      sx={{ flex: '1 1 180px' }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleAdd(branch.key)}
                      sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}
                    >
                      Ekle
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSave(branch.key)}
                      disabled={draft.saving || !draft.dirty}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        height: 40,
                        bgcolor: '#128C7E',
                        '&:hover': { bgcolor: '#0e6e63' },
                      }}
                    >
                      {draft.saving ? 'Kaydediliyor…' : 'Kaydet'}
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
